import React, { createContext, useContext, useCallback, useReducer, useMemo } from 'react';
import { ServerURL } from '../components/ServerURL';

// Action types for the reducer
const ACTIONS = {
  OPEN_MODAL: 'OPEN_MODAL',
  CLOSE_MODAL: 'CLOSE_MODAL',
  SET_LOADING: 'SET_LOADING',
  UPDATE_SPEAKER_COLOR: 'UPDATE_SPEAKER_COLOR',
  SET_SPEAKER_COLORS: 'SET_SPEAKER_COLORS',
  SET_MEETING_COLORS: 'SET_MEETING_COLORS',
  LOAD_CACHED_COLORS: 'LOAD_CACHED_COLORS'
};

// Initial state
const initialState = {
  isOpen: false,
  speaker: null,
  currentColor: null,
  meetingId: null,
  isLoading: false,
  speakerColors: {}, // Global speaker colors cache
  colorVersion: 0, // Version counter for color updates
  meetingColorCache: {}, // Cache colors per meeting: { meetingId: { speaker: color } }
  loadedMeetings: new Set() // Track which meetings have had colors loaded
};

// Reducer for managing color picker state
const colorPickerReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.OPEN_MODAL:
      return {
        ...state,
        isOpen: true,
        speaker: action.payload.speaker,
        currentColor: action.payload.currentColor,
        meetingId: action.payload.meetingId
      };
    
    case ACTIONS.CLOSE_MODAL:
      return {
        ...state,
        isOpen: false,
        speaker: null,
        currentColor: null,
        meetingId: null,
        isLoading: false
      };
    
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case ACTIONS.UPDATE_SPEAKER_COLOR:
      const { speaker, color } = action.payload;
      return {
        ...state,
        speakerColors: {
          ...state.speakerColors,
          [speaker]: color
        },
        colorVersion: state.colorVersion + 1
      };
    
    case ACTIONS.SET_SPEAKER_COLORS:
      return {
        ...state,
        speakerColors: action.payload,
        colorVersion: state.colorVersion + 1
      };
    
    case ACTIONS.SET_MEETING_COLORS:
      const { meetingId, colors } = action.payload;
      return {
        ...state,
        meetingColorCache: {
          ...state.meetingColorCache,
          [meetingId]: colors
        },
        loadedMeetings: new Set([...state.loadedMeetings, meetingId]),
        speakerColors: colors, // Set current active colors
        colorVersion: state.colorVersion + 1
      };
    
    case ACTIONS.LOAD_CACHED_COLORS:
      const cachedColors = state.meetingColorCache[action.payload.meetingId];
      if (cachedColors) {
        return {
          ...state,
          speakerColors: cachedColors,
          colorVersion: state.colorVersion + 1
        };
      }
      return state;
    
    default:
      return state;
  }
};

// Create contexts - split modal state from speaker colors and actions
const ColorPickerContext = createContext(); // For the modal component only
const SpeakerColorsContext = createContext(); // For components that need colors
const ColorPickerActionsContext = createContext(); // For components that need to open modal (stable)

// Provider component
export const ColorPickerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(colorPickerReducer, initialState);

  // Open color picker modal
  const openColorPicker = useCallback((speaker, currentColor, meetingId) => {
    dispatch({
      type: ACTIONS.OPEN_MODAL,
      payload: { speaker, currentColor, meetingId }
    });
  }, []);

  // Close color picker modal
  const closeColorPicker = useCallback(() => {
    dispatch({ type: ACTIONS.CLOSE_MODAL });
  }, []);

  // Update speaker color (optimistic update + server sync)
  const updateSpeakerColor = useCallback(async (speaker, newColor, meetingId) => {
    // Optimistic update
    dispatch({
      type: ACTIONS.UPDATE_SPEAKER_COLOR,
      payload: { speaker, color: newColor }
    });

    // Set loading state
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });

    try {
      const response = await fetch(
        `${ServerURL}/set_speaker_color/${meetingId}/${encodeURIComponent(speaker)}/${encodeURIComponent(newColor)}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (!response.ok) {
        // Revert optimistic update on failure - we'll need to handle this differently
        // For now, just log the error - the optimistic update will remain
        console.error('Failed to update speaker color on server');
        throw new Error('Failed to update speaker color');
      }

      // Update the meeting cache with the new color
      if (meetingId) {
        const currentMeetingColors = state.meetingColorCache[meetingId] || {};
        dispatch({
          type: ACTIONS.SET_MEETING_COLORS,
          payload: {
            meetingId,
            colors: { ...currentMeetingColors, [speaker]: newColor }
          }
        });
      }

      // Close modal on success
      closeColorPicker();
    } catch (error) {
      console.error('Error updating speaker color:', error);
      // Could add error state here if needed
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [closeColorPicker, state.meetingColorCache]);

  // Set all speaker colors (for initial load) - deprecated, use setMeetingColors instead
  const setSpeakerColors = useCallback((colors) => {
    dispatch({
      type: ACTIONS.SET_SPEAKER_COLORS,
      payload: colors
    });
  }, []);

  // Set colors for a specific meeting (with caching)
  const setMeetingColors = useCallback((meetingId, colors) => {
    dispatch({
      type: ACTIONS.SET_MEETING_COLORS,
      payload: { meetingId, colors }
    });
  }, []);

  // Load cached colors for a meeting (if available)
  const loadCachedColors = useCallback((meetingId) => {
    dispatch({
      type: ACTIONS.LOAD_CACHED_COLORS,
      payload: { meetingId }
    });
  }, []);

  // Check if meeting colors are already loaded
  const isMeetingColorsLoaded = useCallback((meetingId) => {
    return state.loadedMeetings.has(meetingId);
  }, [state.loadedMeetings]);

  // Get color for a speaker
  const getSpeakerColor = useCallback((speaker, fallbackColors = []) => {
    return state.speakerColors[speaker] || fallbackColors[0] || '#a6cee3';
  }, [state.speakerColors]);

  // Stable actions context - never changes, so components using this won't re-render
  const actionsContextValue = useMemo(() => ({
    openColorPicker
  }), [openColorPicker]);

  // Modal context for the modal component only - includes all state and actions
  const modalContextValue = useMemo(() => ({
    // Modal state - only for modal components
    isOpen: state.isOpen,
    speaker: state.speaker,
    currentColor: state.currentColor,
    meetingId: state.meetingId,
    isLoading: state.isLoading,
    
    // Modal actions
    openColorPicker,
    closeColorPicker,
    updateSpeakerColor
  }), [
    state.isOpen,
    state.speaker, 
    state.currentColor,
    state.meetingId,
    state.isLoading,
    openColorPicker,
    closeColorPicker,
    updateSpeakerColor
  ]);

  // Memoize speaker colors context value to prevent rerenders when modal state changes
  const speakerColorsContextValue = useMemo(() => ({
    speakerColors: state.speakerColors,
    colorVersion: state.colorVersion,
    setSpeakerColors, // Deprecated - for backward compatibility
    setMeetingColors,
    loadCachedColors,
    isMeetingColorsLoaded,
    getSpeakerColor
  }), [state.speakerColors, state.colorVersion, setSpeakerColors, setMeetingColors, loadCachedColors, isMeetingColorsLoaded, getSpeakerColor]);

  return (
    <ColorPickerContext.Provider value={modalContextValue}>
      <SpeakerColorsContext.Provider value={speakerColorsContextValue}>
        <ColorPickerActionsContext.Provider value={actionsContextValue}>
          {children}
        </ColorPickerActionsContext.Provider>
      </SpeakerColorsContext.Provider>
    </ColorPickerContext.Provider>
  );
};

// Custom hook for modal functionality (used by GlobalColorPickerModal ONLY)
export const useColorPickerModal = () => {
  const context = useContext(ColorPickerContext);
  if (!context) {
    throw new Error('useColorPickerModal must be used within a ColorPickerProvider');
  }
  return context;
};

// Custom hook for modal actions only (used by TranscriptionCard) - stable, won't cause re-renders
export const useColorPickerActions = () => {
  const context = useContext(ColorPickerActionsContext);
  if (!context) {
    throw new Error('useColorPickerActions must be used within a ColorPickerProvider');
  }
  return context;
};

// Custom hook for speaker colors only (used by TranscriptionViewer and other components that need colors)
export const useSpeakerColors = () => {
  const context = useContext(SpeakerColorsContext);
  if (!context) {
    throw new Error('useSpeakerColors must be used within a ColorPickerProvider');
  }
  return context;
};

// Custom hook for a specific speaker's color - only re-renders when that speaker's color changes
export const useSpeakerColor = (speaker, fallbackColors = []) => {
  const { speakerColors } = useSpeakerColors();
  
  // Extract the speaker's specific color to avoid complex dependency
  const speakerSpecificColor = speakerColors[speaker];
  
  return useMemo(() => {
    return speakerSpecificColor || fallbackColors[0] || '#a6cee3';
  }, [speakerSpecificColor, fallbackColors]);
};

// Legacy hook for backward compatibility - combines both contexts
export const useColorPicker = () => {
  const modalContext = useColorPickerModal();
  const colorsContext = useSpeakerColors();
  
  return {
    ...modalContext,
    ...colorsContext
  };
};

export default ColorPickerContext;